<template>
  <div class="container mx-auto">
    <div class="mx-4 mt-8">
      <doe-page-heading class="mb-2" label="Register your service" description="Enter the full Service Approval Number (SE-00000000) for your service below." />
      <div class="max-w-6xl">
      <doe-paragraph
        description="Please note: only the person who has authority to be the administrator for this service should complete this step. The administrator is responsible for managing access for users (educators) within the Self-Assessment and Quality Improvement Planning 
Portal platform."
        class="mb-6"
      />
      </div>
      <div class="mb-6">
        <label for="query"
          class="block font-light font-sans leading-snug mb-2 subpixel-antialiased text-xl text-black w-full"
        >
          Enter your Service Approval Number (SE-00000000)
        </label>
        <input
          id="query"
          class="mb-4 lg:mb-0 bg-gray-300 border border-gray-800 rounded font-sans leading-snug max-w-2xl px-3 py-2 subpixel-antialiased text-gray-900 text-xl w-full"
          name="query"
          v-model="q"
          v-validate="{ required: true, regex: /^(?:SE|se)-\d{8}$/, excluded: registeredServiceNumbers }"
          data-vv-validate-on="input"
          data-vv-as="SE-Number"
        />
        <doe-button
          :disabled="!isFormInvalid"
          class="block lg:inline-block lg:ml-2 max-w-2xl"
          appearance="secondary"
          size="small"
          iconSize="small"
          @click="serviceSearch()"
        >
          Search
        </doe-button>
        <doe-error v-if="errors.firstByRule('query', 'excluded')" description="query">The SE-Number entered is already registered</doe-error>
        <doe-error v-else-if="errors.has('query')" description="query">{{ errors.first('query') }}</doe-error>
      </div>
      <div v-if="isServiceValid" class="mb-6">
        <div>
          <h4 class="block font-sans font-semibold leading-normal max-w-4xl subpixel-antialiased text-xl text-blue-900 w-full">Service found</h4>
          <p class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-2">You are about to be registered as the administrator for the following service. If you are not the administrator for this service, do not proceed.</p>
        </div>

        <p class="block font-sans leading-snug subpixel-antialiased text-base text-black font-semibold">{{ service[0].tradingAsName }}</p>
        <p class="block font-sans leading-snug subpixel-antialiased text-sm text-black font-light">{{ service[0].addresslineOne }}
        {{ service[0].suburb }}
        {{ service[0].state }}
        {{ service[0].postcode }}</p>
      </div>
      <div v-if="isServiceNotFound" class="mb-6">
        <doe-error description="service-not-found">Service ID not found. Please check your Service ID and try again.</doe-error>
      </div>

      <div>
        <doe-button
          v-if="isUserOnboarded"
          class="mr-4 mb-2"
          appearance="secondary"
          size="large"
          prefix="chevronLeft"
          iconSize="small"
          @click="navServices()"
        >
          Back
        </doe-button>

        <doe-button
          :disabled="isServiceValid"
          appearance="primary"
          size="large"
          iconSize="small"
          @click="registerService()"
        >
          Register
        </doe-button>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { serviceCallWithAuth } from "@/helpers/cognito";
import handleRoles from "@/mixins/handleRoles";
import DoePageHeading from "@/components/typography/PageHeading";
import DoeParagraph from "@/components/typography/Paragraph";
import DoeError from "@/components/field/Error";
import DoeButton from "@/components/button/Button";

export default {
  name: "RegisterService",
  mixins: [handleRoles],
  components: {
    DoePageHeading,
    DoeParagraph,
    DoeError,
    DoeButton
  },
  inject: {
    $validator: "$validator"
  },
  data() {
    return {
      q: "",
      service: {},
      searchPerformed: false
    };
  },
  watch: {
    q() {
      this.service = {};
      this.searchPerformed = false;
    }
  },
  computed: {
    profile() {
      return this.$OAuth.data.profile;
    },
    aclArr() {
      if(this.profile) {
        return Array.isArray(this.profile.acl) ? this.profile.acl : [this.profile.acl];
      }
      return [];
    },
    registeredServiceNumbers () {
      // return null
      return this.aclArr.map(se => se.split(" ")[0])
    },
    isUserOnboarded() {
      if (
        this.aclArr.includes("SE-00000000 TTSDS Director") &&
        this.aclArr.length === 1
      ) {
        return false;
      } else {
        return true;
      }
    },
    isFormInvalid() {
      if (this.q.length === 0) {
        return true;
      }
      const validPattern = /^(?:SE|se)-\d{8}$/;
      if (!validPattern.test(this.q)) {
        return true;
      }
      if (this.errors.has("query")) {
        return true;
      }
      return false;
    },
    isServiceValid() {
      return Array.isArray(this.service) &&
        this.service.length > 0 &&
        this.service[0] != null &&
        !!this.service[0].tradingAsName;
    },
    isServiceNotFound() {
      return this.searchPerformed && !this.isServiceValid;
    },
    isFDC() {
      return this.service && this.service.serviceType.trim() === "Family Day Care";
    }
  },
  methods: {
    ...mapActions(["SET_REGISTER_SERVICE"]),
    serviceSearch() {
      this.$root.isLoading = true;
      let headers = this.$OAuth.buildHttpHeader();
      let options = {
        path: "/centre",
        searchParams: { seNumber: this.q.toUpperCase() },
        headers: headers
      };
      serviceCallWithAuth("doe-ttss", options)
        .then(result => {
          console.log("calling service search...")
          this.service = result.body;
        })
        .catch((err) => {
          this.service = {};
          this.$root.translateModal(
            "For more information, please contact the department’s Information and Enquiries team on 1800 619 113 (toll free) or at information@earlylearningcommission.nsw.gov.au.",
            `Your service can't be registered: ${err?.message}`
          );
        })
        .finally(() => {          this.searchPerformed = true;          this.$root.isLoading = false;
        });
    },
    registerService() {
      this.SET_REGISTER_SERVICE(this.service);
      this.$root.isLoading = true;
      let headers = this.$OAuth.buildHttpHeader();
    
      let body = JSON.stringify({
        seNumber: this.service[0].serviceId,
        action: "VERIFY",
        requestedByUserId: this.profile.detuserid
      });
      let options = {
        path: "/director-pin",
        method: "POST",
        body: body,
        headers: headers
      };
      serviceCallWithAuth("doe-ttss", options)
        .then(() => {
          this.$router.push({ name: "service-verify" ,
          params: { email: this.service[0].email}
            });
        })
        .catch(() => {
          this.service = {};
          this.$root.translateModal(
            "For more information, please contact the department’s Information and Enquiries team on 1800 619 113 (toll free) or at information@earlylearningcommission.nsw.gov.au.",
            "Your service can't be registered"
          );
        })
        .finally(() => {
          this.$root.isLoading = false;
        });
    },
    navServices() {
      this.$router.push({name: "service-dashboard"});
    }
  }
};
</script>

<style lang="scss" scoped>
.primary:focus {
  outline: 1px solid #ce0037;
  outline-offset: 3px;
}
.primary:disabled,
.primary[disabled] {
  @apply cursor-not-allowed bg-red-800 border-red-800;
}
.secondary:focus {
  outline: 1px solid #041e42;
  outline-offset: 3px;
}
.secondary:disabled,
.secondary[disabled] {
  @apply cursor-not-allowed text-gray-700 bg-gray-400 border-gray-700;
}
</style>
